import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createStoreSelectors } from '@/stores/createStoreSelectors';
import type {
  Asset,
  Basin,
  Company,
  Country,
  DevelopmentStatus,
  DevelopmentType,
  ShoreStatus,
} from '@/types/GraphqlSchema';
import { FilterKey } from '../components/Filters/filterConfig';

type HydrocarbonFilter = {
  id?: string | number;
  name: string;
  type?: string | null;
  oil?: string | null;
};

type GeologyFilter = {
  epoch?: {
    id?: string;
  };
};

type ValuationsFilters = {
  [FilterKey.NpvMin]: string;
  [FilterKey.NpvMax]: string;
  [FilterKey.ReservesMin]: string;
  [FilterKey.ReservesMax]: string;
  [FilterKey.ProductionMin]: string;
  [FilterKey.ProductionMax]: string;
  [FilterKey.DevelopmentStatuses]: DevelopmentStatus[];
  [FilterKey.DevelopmentTypes]: DevelopmentType[];
  [FilterKey.DepthMin]: string;
  [FilterKey.DepthMax]: string;
  [FilterKey.Assets]: Asset[];
  [FilterKey.Countries]: Country[];
  [FilterKey.Companies]: Company[];
  [FilterKey.Basins]: Basin[];
  [FilterKey.ShoreStatuses]: ShoreStatus[];
  [FilterKey.Hydrocarbons]: HydrocarbonFilter[];
  [FilterKey.Geologies]?: GeologyFilter[];
  [FilterKey.IncludeHistoricalParticipants]: boolean;
  [FilterKey.Operator]: boolean;
  [FilterKey.NonOperator]: boolean;
  [FilterKey.Opportunities]: boolean;
  [FilterKey.CompanyOperand]?: 'AND' | 'OR';
};

export const createDefaultFilters = (): ValuationsFilters => ({
  [FilterKey.NpvMin]: '',
  [FilterKey.NpvMax]: '',
  [FilterKey.ReservesMin]: '',
  [FilterKey.ReservesMax]: '',
  [FilterKey.ProductionMin]: '',
  [FilterKey.ProductionMax]: '',
  [FilterKey.DevelopmentStatuses]: [],
  [FilterKey.DevelopmentTypes]: [],
  [FilterKey.DepthMin]: '',
  [FilterKey.DepthMax]: '',
  [FilterKey.Assets]: [],
  [FilterKey.Countries]: [],
  [FilterKey.Companies]: [],
  [FilterKey.Basins]: [],
  [FilterKey.ShoreStatuses]: [],
  [FilterKey.Hydrocarbons]: [],
  [FilterKey.IncludeHistoricalParticipants]: false,
  [FilterKey.Operator]: false,
  [FilterKey.NonOperator]: false,
  [FilterKey.Opportunities]: false,
  [FilterKey.CompanyOperand]: 'OR',
});

type SortType = {
  label: string;
  filter: string;
};

type AssetsPageInfo = {
  endCursor: string | null;
  hasNextPage: boolean;
};

type ValuationsState = {
  filters: ValuationsFilters;
  selectedAsset: Asset | null;
  selectedAssetDetails: Asset | null;
  sortedAssets: Asset[];
  totalCount: number;
  watchListView: boolean;
  watchlistAssetIds: Set<string>;

  sortType: SortType;
  sortAscending: boolean;
  loadingAssets: boolean;
  loadingAssetDetails: boolean;
  loadingMoreAssets: boolean;
  assetsPageInfo: AssetsPageInfo;

  setFilters: (
    newFilters: ValuationsFilters | ((prev: ValuationsFilters) => ValuationsFilters),
  ) => void;
  clearFilters: () => void;
  setSelectedAsset: (asset: Asset | null) => void;
  setSelectedAssetDetails: (asset: Asset | null) => void;
  setSortedAssets: (assets: Asset[] | ((prev: Asset[]) => Asset[])) => void;
  setTotalCount: (count: number) => void;
  setSortType: (sortType: SortType) => void;
  setSortAscending: (ascending: boolean) => void;
  setLoadingAssets: (loading: boolean) => void;
  setLoadingAssetDetails: (loading: boolean) => void;
  setLoadingMoreAssets: (loading: boolean) => void;
  setAssetsPageInfo: (pageInfo: AssetsPageInfo) => void;
  setWatchListView: (view: boolean) => void;
  setWatchlistAssetIds: (ids: Set<string>) => void;
};

const valuationsStoreBase = create(
  devtools<ValuationsState>(
    set => ({
      filters: createDefaultFilters(),
      selectedAsset: null,
      selectedAssetDetails: null,
      sortedAssets: [],
      totalCount: 0,
      watchListView: false,
      watchlistAssetIds: new Set<string>(),
      sortType: { label: 'NPV', filter: 'npv' },
      sortAscending: false,

      loadingAssets: false,
      loadingAssetDetails: false,
      loadingMoreAssets: false,
      assetsPageInfo: { endCursor: null, hasNextPage: false },

      setFilters: (
        newFilters: ValuationsFilters | ((prev: ValuationsFilters) => ValuationsFilters),
      ) => {
        set(
          state => ({
            filters: typeof newFilters === 'function' ? newFilters(state.filters) : newFilters,
          }),
          false,
          'setFilters',
        );
      },

      clearFilters: () => {
        set({ filters: createDefaultFilters() }, false, 'clearFilters');
      },

      setSelectedAsset: (asset: Asset | null) =>
        set({ selectedAsset: asset }, false, 'setSelectedAsset'),

      setSelectedAssetDetails: (asset: Asset | null) =>
        set({ selectedAssetDetails: asset }, false, 'setSelectedAssetDetails'),

      setSortedAssets: (assets: Asset[] | ((prev: Asset[]) => Asset[])) =>
        set(
          state => ({
            sortedAssets: typeof assets === 'function' ? assets(state.sortedAssets) : assets,
          }),
          false,
          'setSortedAssets',
        ),

      setTotalCount: (count: number) => set({ totalCount: count }, false, 'setTotalCount'),

      setSortType: (sortType: SortType) => set({ sortType }, false, 'setSortType'),

      setSortAscending: (ascending: boolean) =>
        set({ sortAscending: ascending }, false, 'setSortAscending'),

      setLoadingAssets: (loading: boolean) =>
        set({ loadingAssets: loading }, false, 'setLoadingAssets'),

      setLoadingAssetDetails: (loading: boolean) =>
        set({ loadingAssetDetails: loading }, false, 'setLoadingAssetDetails'),

      setLoadingMoreAssets: (loading: boolean) =>
        set({ loadingMoreAssets: loading }, false, 'setLoadingMoreAssets'),

      setAssetsPageInfo: (pageInfo: AssetsPageInfo) =>
        set({ assetsPageInfo: pageInfo }, false, 'setAssetsPageInfo'),

      setWatchListView: (view: boolean) => set({ watchListView: view }, false, 'setWatchListView'),

      setWatchlistAssetIds: (ids: Set<string>) =>
        set({ watchlistAssetIds: ids }, false, 'setWatchlistAssetIds'),
    }),
    {
      name: 'valuations',
    },
  ),
);

const useValuationsStore = createStoreSelectors(valuationsStoreBase);

export { useValuationsStore, valuationsStoreBase, FilterKey };
export type { HydrocarbonFilter, GeologyFilter, ValuationsFilters };
